import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ArrowLeft, Search, X, MapPin, Clock } from 'lucide-react';
import { useEventModules } from '../hooks/useEventModules';

type RoomType = 'keynote' | 'talk' | 'workshop' | 'panel' | 'exhibit' | 'facility';

interface Room {
  id: string;
  label: string;
  sublabel?: string;
  type: RoomType;
  x: number;
  y: number;
  w: number;
  h: number;
  session?: string;
  sessionTime?: string;
}

const ROOM_TYPE_STYLE: Record<RoomType, { fill: string; stroke: string; text: string }> = {
  keynote:  { fill: '#0EA5E922', stroke: '#0EA5E9',  text: '#7DD3FC' },
  talk:     { fill: '#7C3AED22', stroke: '#7C3AED',  text: '#C4B5FD' },
  workshop: { fill: '#05966922', stroke: '#059669',  text: '#6EE7B7' },
  panel:    { fill: '#D9780622', stroke: '#D97806',  text: '#FCD34D' },
  exhibit:  { fill: '#F5922022', stroke: '#F59220',  text: '#FDBA74' },
  facility: { fill: '#ffffff0d', stroke: '#334155',  text: '#64748B' },
};

const FLOOR1: Room[] = [
  { id: 'reg',     label: 'Registration',  sublabel: 'Check-in',     type: 'facility', x: 10,  y: 10,  w: 380, h: 50  },
  { id: 'main',    label: 'Main Hall',     sublabel: 'Keynote Stage', type: 'keynote',  x: 10,  y: 72,  w: 235, h: 155, session: 'Opening Keynote', sessionTime: '9:00 AM' },
  { id: 'exhibit', label: 'Exhibit Hall',  sublabel: 'Booths A–D',   type: 'exhibit',  x: 257, y: 72,  w: 133, h: 295 },
  { id: 'stagea',  label: 'Stage A',       sublabel: 'Main Talks',   type: 'talk',     x: 10,  y: 239, w: 115, h: 88,  session: 'Future of Gen AI', sessionTime: '10:00 AM' },
  { id: 'stageb',  label: 'Stage B',       sublabel: 'Panels',       type: 'panel',    x: 135, y: 239, w: 110, h: 88,  session: 'AI Ethics Panel', sessionTime: '3:30 PM' },
  { id: 'cafe',    label: 'Cafeteria',     sublabel: 'Meals & Coffee',type: 'facility', x: 135, y: 339, w: 110, h: 70  },
  { id: 'rest1',   label: 'Restrooms',     type: 'facility',          x: 10,  y: 339, w: 113, h: 70  },
  { id: 'lobby',   label: 'Lobby',         sublabel: 'Entrance',     type: 'facility', x: 10,  y: 421, w: 380, h: 45  },
];

const FLOOR2: Room[] = [
  { id: 'lab1',    label: 'Lab 1',         sublabel: 'Hands-on',     type: 'workshop', x: 10,  y: 10,  w: 180, h: 125, session: 'Fine-tuning LLMs', sessionTime: '11:00 AM' },
  { id: 'lab2',    label: 'Lab 2',         sublabel: 'Demos',        type: 'workshop', x: 200, y: 10,  w: 190, h: 125, session: 'RL Workshop',      sessionTime: '10:30 AM' },
  { id: 'r201',    label: 'Room 201',      sublabel: 'Breakout',     type: 'talk',     x: 10,  y: 148, w: 115, h: 90  },
  { id: 'r202',    label: 'Room 202',      sublabel: 'Breakout',     type: 'talk',     x: 136, y: 148, w: 110, h: 90  },
  { id: 'r203',    label: 'Room 203',      sublabel: 'Deep Dives',   type: 'talk',     x: 258, y: 148, w: 132, h: 90,  session: 'Transformer Deep Dive', sessionTime: '9:30 AM' },
  { id: 'brk1',    label: 'Breakout A',    sublabel: 'Networking',   type: 'panel',    x: 10,  y: 250, w: 185, h: 80  },
  { id: 'brk2',    label: 'Breakout B',    sublabel: 'Showcase',     type: 'panel',    x: 205, y: 250, w: 185, h: 80,  session: 'Startup Showcase', sessionTime: '4:00 PM' },
  { id: 'rest2',   label: 'Restrooms',     type: 'facility',          x: 10,  y: 342, w: 95,  h: 60  },
  { id: 'storage', label: 'Storage',       type: 'facility',          x: 115, y: 342, w: 275, h: 60  },
];

const LEGEND: { type: RoomType; label: string }[] = [
  { type: 'keynote',  label: 'Keynote' },
  { type: 'talk',     label: 'Talks' },
  { type: 'workshop', label: 'Workshop' },
  { type: 'panel',    label: 'Panel' },
  { type: 'exhibit',  label: 'Exhibit' },
  { type: 'facility', label: 'Facility' },
];

export default function MapScreen() {
  const navigate = useNavigate();
  const { isEnabled } = useEventModules();
  const [activeFloor, setActiveFloor] = useState<1 | 2>(1);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const rooms = activeFloor === 1 ? FLOOR1 : FLOOR2;
  const viewBoxH = activeFloor === 1 ? 475 : 415;

  const matchedRoom = searchQuery
    ? rooms.find(r => r.label.toLowerCase().includes(searchQuery.toLowerCase()))
    : null;

  const handleRoomClick = (room: Room) => {
    setSelectedRoom(prev => (prev?.id === room.id ? null : room));
  };

  return (
    <div className="size-full flex flex-col overflow-hidden transition-colors duration-300" style={{ backgroundColor: 'var(--app-bg)' }}>
      {/* Header */}
      <div
        className="app-screen-header flex items-center justify-between px-4 pt-12 pb-4 border-b shrink-0"
        style={{ backgroundColor: 'var(--app-bg)', borderColor: 'var(--app-divider)' }}
      >
        <button
          onClick={() => navigate(-1)}
          className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
          style={{ backgroundColor: 'var(--app-surface)' }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
        </button>
        <div className="flex items-center gap-2">
          <MapPin className="w-4 h-4" style={{ color: 'var(--app-accent)' }} />
          <span className="text-base" style={{ color: 'var(--app-text)' }}>Venue Map</span>
        </div>
        <div className="w-10" />
      </div>

      {/* Search + Floor Selector */}
      <div className="px-4 pt-3 pb-3 shrink-0 flex flex-col gap-3" style={{ backgroundColor: 'var(--app-bg)' }}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
          <input
            type="text"
            value={searchQuery}
            onChange={e => {
              setSearchQuery(e.target.value);
              if (e.target.value) {
                const found = rooms.find(r => r.label.toLowerCase().includes(e.target.value.toLowerCase()));
                if (found) setSelectedRoom(found);
              } else {
                setSelectedRoom(null);
              }
            }}
            placeholder="Search rooms..."
            className="w-full pl-10 pr-10 py-2.5 rounded-lg text-sm outline-none transition-colors"
            style={{
              backgroundColor: 'var(--app-input-bg)',
              border: '1px solid var(--app-input-border)',
              color: 'var(--app-text)',
            }}
          />
          {searchQuery && (
            <button
              onClick={() => { setSearchQuery(''); setSelectedRoom(null); }}
              className="absolute right-3 top-1/2 -translate-y-1/2"
              style={{ color: 'var(--app-text-muted)' }}
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="flex gap-2">
          {([1, 2] as const).map(floor => (
            <button
              key={floor}
              onClick={() => { setActiveFloor(floor); setSelectedRoom(null); setSearchQuery(''); }}
              className="flex-1 py-2 rounded-xl text-sm transition-colors"
              style={
                activeFloor === floor
                  ? { backgroundColor: 'var(--app-accent-solid)', color: '#fff' }
                  : { backgroundColor: 'var(--app-surface)', color: 'var(--app-text-muted)', border: '1px solid var(--app-border)' }
              }
            >
              Floor {floor}
            </button>
          ))}
        </div>
      </div>

      {/* Map SVG */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        <div
          className="rounded-2xl border overflow-hidden"
          style={{ backgroundColor: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
        >
          <svg viewBox={`0 0 400 ${viewBoxH}`} className="w-full" style={{ touchAction: 'manipulation' }}>
            <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#ffffff08" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="400" height={viewBoxH} fill="url(#grid)" />
            {rooms.map(room => {
              const style = ROOM_TYPE_STYLE[room.type];
              const isSelected = selectedRoom?.id === room.id || matchedRoom?.id === room.id;
              return (
                <g key={room.id} onClick={() => handleRoomClick(room)} style={{ cursor: 'pointer' }}>
                  <rect
                    x={room.x} y={room.y} width={room.w} height={room.h} rx={6}
                    fill={isSelected ? style.stroke + '44' : style.fill}
                    stroke={style.stroke}
                    strokeWidth={isSelected ? 2 : 1}
                    strokeOpacity={isSelected ? 1 : 0.5}
                  />
                  <text
                    x={room.x + room.w / 2} y={room.y + room.h / 2 - (room.sublabel ? 8 : 0)}
                    textAnchor="middle" dominantBaseline="middle"
                    fill={isSelected ? '#ffffff' : style.text}
                    fontSize={room.w > 140 ? 11 : room.w > 80 ? 10 : 9}
                    fontFamily="Inter, sans-serif" fontWeight={isSelected ? '600' : '500'}
                  >
                    {room.label}
                  </text>
                  {room.sublabel && (
                    <text
                      x={room.x + room.w / 2} y={room.y + room.h / 2 + 10}
                      textAnchor="middle" dominantBaseline="middle"
                      fill={style.text} fontSize={8} opacity={0.7} fontFamily="Inter, sans-serif"
                    >
                      {room.sublabel}
                    </text>
                  )}
                  {isEnabled('agenda') && room.session && (
                    <circle cx={room.x + room.w - 8} cy={room.y + 8} r={4} fill="#ef4444" opacity={0.9} />
                  )}
                </g>
              );
            })}
          </svg>
        </div>

        {/* Selected Room Info */}
        {selectedRoom && (
          <div
            className="mt-3 rounded-xl p-4 border"
            style={{ backgroundColor: 'var(--app-surface)', borderColor: 'var(--app-border)', boxShadow: 'var(--app-card-shadow)' }}
          >
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm" style={{ color: 'var(--app-text)' }}>{selectedRoom.label}</p>
                {selectedRoom.sublabel && (
                  <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>{selectedRoom.sublabel}</p>
                )}
              </div>
              <span
                className="text-[10px] px-2 py-0.5 rounded-full border"
                style={{
                  color: ROOM_TYPE_STYLE[selectedRoom.type].text,
                  borderColor: ROOM_TYPE_STYLE[selectedRoom.type].stroke + '80',
                  background: ROOM_TYPE_STYLE[selectedRoom.type].fill,
                }}
              >
                {selectedRoom.type.charAt(0).toUpperCase() + selectedRoom.type.slice(1)}
              </span>
            </div>
            {isEnabled('agenda') && selectedRoom.session && (
              <div className="mt-3 flex flex-col gap-1.5">
                <div className="flex items-center gap-1.5">
                  <div className="size-1.5 bg-[#ef4444] rounded-full" />
                  <p className="text-xs text-[#ef4444]">Happening now</p>
                </div>
                <div className="flex items-center gap-2 text-xs" style={{ color: 'var(--app-text-muted)' }}>
                  <Clock className="w-3 h-3" style={{ color: 'var(--app-accent)' }} />
                  <span>{selectedRoom.session} · {selectedRoom.sessionTime}</span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Legend */}
        <div className="mt-4 flex flex-wrap gap-2">
          {LEGEND.map(({ type, label }) => {
            const style = ROOM_TYPE_STYLE[type];
            return (
              <div
                key={type}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-full border"
                style={{ borderColor: style.stroke + '60', background: style.fill }}
              >
                <div className="size-2 rounded-full" style={{ background: style.stroke }} />
                <span className="text-[10px]" style={{ color: style.text }}>{label}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
